// Helpers PUROS del modo directo de "Crear desde cero".
//
// El modo directo no le pega al LLM: traduce el estado de la UI (prompt,
// tipo de salida, formato, duración, chips de adjuntos) al input de una
// tool card, que después se empuja a mano con `chat.pushToolCard` — mismo
// mecanismo scripted que usa "Sorpréndeme" (InterfazV2). Toda la lógica
// testeable vive acá; los componentes solo la consumen.
//
// Ver ADR-2026-08-19-modo-directo-crear-desde-cero.

import type { ChatAttachment } from '../../agentAttachments';
import { FORMAT_OPTIONS_V2 } from '../../mockData';
import {
  creditsForImage,
  creditsForVideoGeneral,
} from '../../../../utils/credits';

export type OutputKind = 'imagen' | 'video';

/** Rango de duración del video en modo directo (segundos), para el slider.
 *  El motor acepta cualquier número (`AgentVideoConfig.durationSeconds`,
 *  default 5); estos son los límites que ofrecemos en la UI. */
export const VIDEO_DURATION_MIN = 5;
export const VIDEO_DURATION_MAX = 10;

/** Normaliza la duración al rango del slider. */
export function clampDuracion(s: number | undefined): number {
  return Math.max(
    VIDEO_DURATION_MIN,
    Math.min(VIDEO_DURATION_MAX, Math.round(s ?? VIDEO_DURATION_MIN)),
  );
}

/** Tope de imágenes por tanda — espejo de `FREEFORM_MAX_CANTIDAD` de
 *  agentGenerate, que es quien hace el clamp de verdad. */
export const MAX_CANTIDAD = 6;

/** Tope de referencias que acepta el motor (mismo que usa la tool card). */
const MAX_REFERENCES = 6;

export interface DirectCallArgs {
  outputKind: OutputKind;
  prompt: string;
  formatSlug: string;
  /** Imágenes a generar en la tanda (solo aplica a `outputKind: 'imagen'`). */
  cantidad?: number;
  durationSeconds: number;
  attachments: ChatAttachment[];
}

export interface DirectToolCall {
  kind: 'generate_image' | 'generate_video';
  input: Record<string, any>;
}

/** Chips del composer → `reference_images` del schema de las tools.
 *  Las imágenes subidas viajan como `{source:'adjunto'}`: la card las
 *  resuelve desde `getReferenceImages()` del hook, no desde un id. */
function referencesFromAttachments(
  attachments: ChatAttachment[],
): Array<Record<string, string>> {
  const refs: Array<Record<string, string>> = [];
  for (const att of attachments) {
    if (att.kind === 'product') {
      refs.push({ source: 'producto', product_id: att.productId });
    } else if (att.kind === 'model') {
      // OJO: `ModelSelection` discrimina por `mode`, no por `kind`.
      // Solo los modelos del catálogo ('preset') tienen id que el motor
      // pueda resolver; 'auto' / 'custom' / 'upload' no aportan referencia.
      const sel = att.selection;
      if (sel && sel.mode === 'preset' && sel.id != null) {
        refs.push({ source: 'modelo', model_id: String(sel.id) });
      }
    } else if (att.kind === 'image') {
      refs.push({ source: 'adjunto' });
    }
  }
  return refs.slice(0, MAX_REFERENCES);
}

/** Normaliza la cantidad al rango que acepta el motor. */
export function clampCantidad(n: number | undefined): number {
  return Math.max(1, Math.min(MAX_CANTIDAD, Math.floor(n ?? 1)));
}

/** Traduce el estado del modo directo a la tool card a empujar. */
export function buildDirectToolCall(args: DirectCallArgs): DirectToolCall {
  const { outputKind, prompt, formatSlug, durationSeconds, attachments } = args;
  const refs = referencesFromAttachments(attachments);
  const isVideo = outputKind === 'video';
  const cantidad = clampCantidad(args.cantidad);

  return {
    kind: isVideo ? 'generate_video' : 'generate_image',
    input: {
      prompt: prompt.trim(),
      format: formatSlug,
      // El video NUNCA autogenera: siempre muestra la card-resumen con el
      // costo antes de gastar créditos (ADR-146/147, respetado a propósito
      // en el modo directo). La imagen sí se dispara sola.
      autogenerate: !isVideo,
      ...(isVideo
        ? { duration: durationSeconds }
        : // Solo mandamos `cantidad` cuando es >1: así una generación simple
          // queda con el mismo input que antes de existir el control.
          cantidad > 1
          ? { cantidad }
          : {}),
      ...(refs.length > 0 ? { reference_images: refs } : {}),
    },
  };
}

/** Créditos que va a costar la generación, para mostrarlos ANTES de tocar
 *  Generar. Misma fuente de precios que el Estudio de Video y que la card
 *  de resumen del video (`utils/credits`). El payload del asistente manda
 *  quality 'estandar' + 720p, así que el video usa esa tarifa. */
export function creditsForDirectCall(args: {
  outputKind: OutputKind;
  cantidad?: number;
  durationSeconds: number;
}): number {
  if (args.outputKind === 'video') {
    return creditsForVideoGeneral({
      durationSec: args.durationSeconds,
      model: 'standard',
      resolution: '720p',
    });
  }
  return creditsForImage() * clampCantidad(args.cantidad);
}

/** Línea de contexto invisible que le avisa al agente qué eligió el usuario
 *  en el toggle. Es una PISTA, no una imposición: con Ayuda de IA prendida
 *  el agente sigue pudiendo conversar y ajustar. */
export function buildOutputKindHintLine(kind: OutputKind): string {
  return kind === 'video'
    ? 'El usuario tiene seleccionado "video" en el toggle — salvo que pida explícitamente otra cosa, generá un video.'
    : 'El usuario tiene seleccionado "imagen" en el toggle — salvo que pida explícitamente otra cosa, generá una imagen.';
}

/** Label compacto para la pill de formato: el ratio entre paréntesis
 *  ("Cuadrado (1:1)" → "1:1"). Los nombres completos no entran en la fila. */
export function formatShortLabel(id: string | null): string {
  const opt = FORMAT_OPTIONS_V2.find((f) => f.id === id);
  const ratio = opt?.name.match(/\(([^)]+)\)/)?.[1];
  return ratio ?? '1:1';
}
